import {
  getDisplayCalendarSyncTile,
  getHasUserGivenCalendarConsent,
  getIsLoadingParkingReservations,
  getIsLoadingSettings,
  getIsLoadingWorkspaceReservations,
  getIsMeetingsCalendarEnabled,
  getIsUserCalendarConsentInvalid,
  getIsWorkdayLoading,
  getMeetingPreferencesLoadingStatus,
  getWorkdayByDate,
} from '../../data';
import {useAppSelector} from '../../hooks';
import {FeatureMap} from '../../infrastructure';
import {getIsHMEnabledAndIntegrationEnabled} from '../meetings';
import {useHomePageSections_timeslots} from './timeslots/useHomePageSections_timeslots';
import {PartOfDay} from './useHomeTime';

// useXReady -> we don't need all data, but we do need to know enough for the component to be able
//              to render at a fixed height

// Note: these could be selectors but I feel they are too tied to UI to really be reusable
const useWorkdayTileReady = () => {
  // Different sizes based on which 3 sections are available
  // If we implement a design that has the same height for all combinations, we can simply return true here.
  // For now, I think it's better to only add sections if they are relevant (e.g. don't add a map if there's
  // no workday)
  const workdayReady = !useAppSelector(getIsWorkdayLoading);
  const workspaceReady = !useAppSelector(getIsLoadingWorkspaceReservations);
  const parkingReady = !useAppSelector(getIsLoadingParkingReservations);

  return workdayReady && workspaceReady && parkingReady;
};

export const useWorkdayTileRelevant = () => {
  return true;
};

export const useUpcomingMeetingsTileReady = () => {
  const meetingSettingsReady = !useAppSelector(getIsLoadingSettings);
  const consentRequestReady = useAppSelector(getMeetingPreferencesLoadingStatus) === 'Loaded';

  return meetingSettingsReady && consentRequestReady;
};

export const useUpcomingMeetingsTileRelevant = (enabledFeatures: FeatureMap) => {
  const {HybridMeetingsCalendarView: hybridMeetingsFeatureEnabled} = enabledFeatures;
  const isMeetingCalendarEnabled = useAppSelector(getIsMeetingsCalendarEnabled);
  const isMeetingCalendarValid = !useAppSelector(getIsUserCalendarConsentInvalid);
  const calendarAccessGranted = useAppSelector(getHasUserGivenCalendarConsent);

  return hybridMeetingsFeatureEnabled && isMeetingCalendarEnabled && isMeetingCalendarValid && calendarAccessGranted;
};

export const useMeetingConsentTileReady = () => {
  return useAppSelector(getMeetingPreferencesLoadingStatus) === 'Loaded';
};

export const useMeetingConsentTileRelevant = (enabledFeatures: FeatureMap) => {
  const {HybridMeetingsCalendarView: hybridMeetingsFeatureEnabled} = enabledFeatures;

  const isMeetingCalendarInValid = useAppSelector(getIsUserCalendarConsentInvalid);
  const isMeetingCalendarEnabled = useAppSelector(getIsMeetingsCalendarEnabled);
  const hasUserGivenConsent = useAppSelector(getHasUserGivenCalendarConsent);
  const displayCalendarSyncTile = useAppSelector(getDisplayCalendarSyncTile);

  return (
    hybridMeetingsFeatureEnabled &&
    isMeetingCalendarEnabled &&
    displayCalendarSyncTile &&
    (!hasUserGivenConsent || isMeetingCalendarInValid)
  );
};

export const useAvailableRoomsRelevant = (dateKey: string, enabledFeatures: FeatureMap) => {
  const {Occupancy: occupancyFeatureEnabled} = enabledFeatures;
  const isMeetingCalendarEnabled = useAppSelector(getIsHMEnabledAndIntegrationEnabled);
  const availabilityFeatureEnabled = isMeetingCalendarEnabled || occupancyFeatureEnabled;

  const workday = useAppSelector((state) => getWorkdayByDate(state, dateKey));
  const mightBeInTheOffice = !workday || workday.status === 'OfficeDay';

  return availabilityFeatureEnabled && mightBeInTheOffice;
};

const useAvailableRoomsReady = () => {
  // We only show it if we know there is an in office workday
  const workdayReady = !useAppSelector(getIsWorkdayLoading);

  return workdayReady;
};

export const useEmployeeCommunicationAnouncementReady = () => {
  // We only show it if we know there is an in office workday
  const workdayReady = !useAppSelector(getIsWorkdayLoading);

  return workdayReady;
};
export const useEmployeeCommunicationAnouncementRelevant = (enabledFeatures: FeatureMap) =>
  !!enabledFeatures.EmployeeCommunication;

export const useNextWorkdaysReady = () => true;

export const useExtrasRelevant = (enabledFeatures: FeatureMap) => !!enabledFeatures.ExtrasFeature;

export const useExtrasReady = () => !useAppSelector((state) => state.data.extras.isLoading);

export const useCustomerBannerRelevant = (enabledFeatures: FeatureMap) => !!enabledFeatures.CustomerBranding;
export const useCustomerBannerReady = () => true;

export const useHomePageSections = (dateKey: string, partOfDay: PartOfDay, enabledFeatures: FeatureMap) => {
  const nonTimeslotsOverview = [
    {
      componentKey: 'CustomerBanner',
      ready: useCustomerBannerReady(),
      relevant: useCustomerBannerRelevant(enabledFeatures),
    },
    {
      componentKey: 'EmployeeCommunicationAnouncement',
      ready: useEmployeeCommunicationAnouncementReady(),
      relevant: useEmployeeCommunicationAnouncementRelevant(enabledFeatures),
    },
    {
      componentKey: 'WorkdayTile',
      ready: useWorkdayTileReady(),
      relevant: useWorkdayTileRelevant(),
    },
    {
      componentKey: 'NextOfficeDaysTileSimplified',
      ready: useNextWorkdaysReady(),
      relevant: true,
    },

    {
      componentKey: 'UpcomingMeetingsTile',
      ready: useUpcomingMeetingsTileReady(),
      relevant: useUpcomingMeetingsTileRelevant(enabledFeatures),
    },

    {
      componentKey: 'MeetingConsentTile',
      ready: useMeetingConsentTileReady(),
      relevant: useMeetingConsentTileRelevant(enabledFeatures),
    },

    {
      componentKey: 'AvailableRoomsTile',
      ready: useAvailableRoomsReady(),
      relevant: useAvailableRoomsRelevant(dateKey, enabledFeatures) && partOfDay === 'WorkingHours',
    },

    {
      componentKey: 'ConnectionsTile',
      ready: true,
      relevant: true,
    },

    {
      componentKey: 'ExtrasTile',
      ready: useExtrasReady(),
      relevant: useExtrasRelevant(enabledFeatures),
    },
  ] as const;

  const timeslotsOverview = useHomePageSections_timeslots(dateKey, partOfDay, enabledFeatures);

  const overview = enabledFeatures.TimeslotsApi ? timeslotsOverview : nonTimeslotsOverview;

  // Return up to the first that is not ready
  const firstLoadingIndex = overview.findIndex((t) => !t.ready);

  const toRender = firstLoadingIndex === -1 ? overview : overview.slice(0, firstLoadingIndex);

  return toRender.filter((r) => r.relevant);
};
